// Per-calculator SEO content: how-it-works copy, reference tables, FAQs,
// and related-calculator cross-links. Rendered below each calculator by
// SEOContentSection and baked into static HTML by react-snap at build time.
//
// FAQ entries here are the single source of truth for both the visible FAQ
// section AND the FAQPage JSON-LD emitted by SEOHead — Google requires that
// FAQ schema match content that is visible on the page.
//
// Keys match calculatorConfig keys in features.js.

import { BRAND } from './brand';

// FAQs shown on the landing page (visible) and emitted as homepage FAQPage schema.
export const landingFaqs = [
  {
    q: 'How much space does video take up?',
    a: 'Video file sizes depend on resolution, codec, bitrate, and duration. For example, 1 hour of UHD ProRes 422 HQ is roughly 318 GB, while 1 hour of H.264 at 50 Mbps is about 22.5 GB. Use the Duration ↔︎ File Size or Camera ↔︎ File Size calculators to estimate exact file sizes for any format.',
  },
  {
    q: 'How do I calculate video file size?',
    a: 'Multiply the bitrate by the duration: File Size = Bitrate × Duration ÷ 8 (divide by 8 to convert bits to bytes). For professional camera codecs like ProRes, ARRIRAW, or REDCODE, the Camera ↔︎ File Size calculator includes real data rates for ARRI, RED, Blackmagic, Sony, Canon, and more.',
  },
  {
    q: 'How much storage do I need for video production?',
    a: 'Storage needs vary with camera, codec, resolution, and shoot duration — a single day of RAW cinema camera footage can generate multiple terabytes before backups. The Dailies Calculator and Camera ↔︎ File Size calculator help you plan storage for original camera files, proxies, and redundant copies.',
  },
  {
    q: 'Are these calculators free?',
    a: `Yes. Every DataCalc tool is free to use, works in any modern browser, and can be installed as an offline-capable app. DataCalc is built and maintained by ${BRAND}, makers of professional post-production software.`,
  },
  {
    q: 'How long can I record on my camera card?',
    a: 'Recording time depends on your camera, codec, resolution, frame rate, and card capacity. As a rule of thumb, divide card capacity by the codec data rate — the Camera ↔︎ File Size calculator does this for you with data rates for popular cinema and mirrorless cameras.',
  },
];

const seoContent = {
  duration: {
    intro: [
      'The Duration ↔︎ File Size calculator converts between video runtime and storage in both directions: enter a duration to see how much space a recording will take, or enter an available file size to see how many minutes you can store. It works with any codec and resolution because file size is a pure function of data rate and time.',
      'Editors and producers use it to budget storage for a project, DITs use it to sanity-check offloads, and camera assistants use it to answer the classic on-set question: "will this take fit on the card?"',
    ],
    formula: {
      label: 'File size from duration and bitrate',
      expression: 'File Size (GB) = Bitrate (Mb/s) × Duration (seconds) ÷ 8,000',
      example: 'Example: a 10-minute clip at 100 Mb/s → 100 × 600 ÷ 8,000 = 7.5 GB.',
    },
    table: {
      caption: 'Approximate storage per hour for common codecs',
      headers: ['Codec / setting', 'Data rate', 'Storage per hour'],
      rows: [
        ['H.264, 1080p streaming quality', '10 Mb/s', '4.5 GB'],
        ['H.264, high-quality 1080p', '50 Mb/s', '22.5 GB'],
        ['H.265/HEVC, UHD acquisition', '100 Mb/s', '45 GB'],
        ['ProRes 422, 1080p @ 23.976', '≈117 Mb/s', '≈53 GB'],
        ['ProRes 422 HQ, 1080p @ 23.976', '≈176 Mb/s', '≈79 GB'],
        ['ProRes 422 HQ, UHD @ 23.976', '≈707 Mb/s', '≈318 GB'],
      ],
      note: 'Data rates are approximate; actual sizes vary slightly with content and encoder.',
    },
    faqs: [
      {
        q: 'How big is one hour of 4K video?',
        a: 'It depends entirely on the codec. One hour of UHD footage is roughly 45 GB at H.265 100 Mb/s, about 318 GB in ProRes 422 HQ, and well over a terabyte in many RAW formats. Enter your exact codec and bitrate in the calculator above for a precise number.',
      },
      {
        q: 'How much video fits on a 1 TB drive?',
        a: 'At 50 Mb/s (high-quality H.264), about 44 hours. In ProRes 422 HQ UHD, roughly 3 hours. Divide drive capacity by the storage-per-hour figure for your codec — or run the calculation in reverse with this tool by entering the file size.',
      },
      {
        q: 'Why is my file smaller than the calculator predicts?',
        a: 'Variable-bitrate (VBR) codecs like H.264 and H.265 use less data on simple or static images, so real files often come in under the nominal bitrate. Constant-bitrate professional codecs like ProRes and DNxHR track their published data rates much more closely.',
      },
      {
        q: 'Does frame rate change file size?',
        a: 'Yes — for intraframe codecs like ProRes, data rate scales roughly linearly with frame rate: 59.94p ProRes is about 2.5× the size of 23.976p at the same resolution. For long-GOP codecs the encoder targets a set bitrate, so frame rate matters less than the bitrate you choose.',
      },
    ],
    related: ['bitrate', 'camera', 'transferTime'],
  },

  camera: {
    intro: [
      'The Camera ↔︎ File Size calculator estimates recording file sizes and card capacity using real data rates for professional cameras — ARRI, RED, Blackmagic, Sony, Canon, Panasonic, DJI, and more. Pick a camera, codec, resolution, and frame rate, and it tells you how much storage a take, an hour, or a full shoot day will consume, or how many minutes fit on a given card.',
      'Camera RAW formats (ARRIRAW, REDCODE, BRAW, X-OCN) and on-board ProRes each have very different appetites: the same one-hour interview might be 200 GB on one setup and well over a terabyte on another. Checking before the shoot beats reformatting cards mid-take.',
    ],
    formula: {
      label: 'Recording time from card capacity',
      expression: 'Recording time (min) ≈ Card capacity (GB) × 8,000 ÷ Bitrate (Mb/s) ÷ 60',
      example: 'Example: a 512 GB card at 400 Mb/s → 512 × 8,000 ÷ 400 ÷ 60 ≈ 170 minutes.',
    },
    table: {
      caption: 'Recording time per 1 TB of storage, by data rate',
      headers: ['Data rate', 'Typical of', 'Time per 1 TB'],
      rows: [
        ['100 Mb/s', 'Mirrorless H.265 / XAVC', '≈22 hours'],
        ['400 Mb/s', 'High-end mirrorless RAW / All-I', '≈5.5 hours'],
        ['800 Mb/s', 'Cinema camera ProRes HQ UHD', '≈2.8 hours'],
        ['1,600 Mb/s', 'Compressed cinema RAW', '≈1.4 hours'],
        ['3,200 Mb/s', 'High-resolution / high-fps RAW', '≈42 minutes'],
      ],
      note: 'Illustrative rates — select your exact camera and codec in the calculator for precise numbers.',
    },
    faqs: [
      {
        q: 'How long can I record on a 512 GB card?',
        a: 'Divide capacity by data rate: at 100 Mb/s (common mirrorless H.265) about 11 hours; at 800 Mb/s (cinema ProRes) around 85 minutes; high-data-rate RAW formats can be under half an hour. Select your camera and codec in the calculator for the exact figure.',
      },
      {
        q: 'What affects camera file size the most?',
        a: 'Codec first, then resolution and frame rate. Moving from a long-GOP codec to RAW can multiply storage 5–10×, doubling resolution roughly quadruples pixel count, and doubling frame rate roughly doubles intraframe data rates.',
      },
      {
        q: 'Do higher frame rates use more storage?',
        a: 'Yes. RAW and intraframe codecs scale almost linearly with frame rate — 120 fps consumes about five times the storage of 24 fps at the same settings.',
      },
      {
        q: 'How much storage does a shoot day need?',
        a: 'A documentary day on mirrorless cameras might produce 250–500 GB, while a narrative day on a RAW cinema camera can exceed 4–6 TB before backup copies. Estimate per-camera daily footage here, then plan total production storage with the Dailies Calculator.',
      },
    ],
    related: ['dailies', 'duration', 'transferTime'],
  },

  bitrate: {
    intro: [
      'The Bitrate ↔︎ File Size calculator converts between data rate, duration, and file size. Use it to work out what a given bitrate produces on disk, or to find the bitrate that fits a delivery size limit — a daily task in streaming encoding, review uploads, and deliverable specs.',
      'Bitrate is measured in megabits per second (Mb/s), while files are measured in bytes — the constant 8 bits per byte is where most back-of-napkin math goes wrong. This tool keeps the units straight for you.',
    ],
    formula: {
      label: 'The bitrate ↔ size relationship',
      expression: 'File Size (GB) = Bitrate (Mb/s) × Duration (s) ÷ 8,000   —   Bitrate (Mb/s) = File Size (GB) × 8,000 ÷ Duration (s)',
      example: 'Example: to fit a 90-minute film in 4 GB → 4 × 8,000 ÷ 5,400 ≈ 5.9 Mb/s.',
    },
    table: {
      caption: 'File size per hour at common bitrates',
      headers: ['Bitrate', 'Per minute', 'Per hour'],
      rows: [
        ['10 Mb/s', '75 MB', '4.5 GB'],
        ['25 Mb/s', '187.5 MB', '11.25 GB'],
        ['50 Mb/s', '375 MB', '22.5 GB'],
        ['100 Mb/s', '750 MB', '45 GB'],
        ['250 Mb/s', '1.87 GB', '112.5 GB'],
        ['500 Mb/s', '3.75 GB', '225 GB'],
      ],
    },
    faqs: [
      {
        q: 'What bitrate should I use for 4K uploads?',
        a: 'For 4K SDR uploads at standard frame rates, platforms like YouTube recommend roughly 35–45 Mb/s (more for HDR or high frame rate). Uploading at higher quality than the recommendation is fine — the platform re-encodes everything anyway.',
      },
      {
        q: 'What is the difference between Mbps and MB/s?',
        a: 'Mb/s (megabits per second) is 8× smaller than MB/s (megabytes per second). A 400 Mb/s codec writes 50 MB every second. Network and codec specs use bits; file sizes and drive speeds usually use bytes.',
      },
      {
        q: 'CBR or VBR — which should I use?',
        a: 'Constant bitrate (CBR) gives predictable file sizes and is often required for broadcast delivery. Variable bitrate (VBR) puts data where the picture needs it and looks better at the same average rate — it is the default for streaming and web delivery.',
      },
      {
        q: 'Does higher bitrate always mean better quality?',
        a: 'Only up to a point. Past the rate a codec needs for the content, extra bits are wasted. A well-encoded H.265 file can match an H.264 file at roughly half the bitrate, so codec choice matters as much as raw rate.',
      },
    ],
    related: ['duration', 'transferTime', 'camera'],
  },

  transferTime: {
    intro: [
      'The Transfer Time calculator estimates how long it takes to copy media between drives, cards, and networks, or upload it over your internet connection. Enter the amount of data and a connection or drive speed, and it returns a realistic transfer time — essential for planning offloads on set and shuttle-drive turnarounds in post.',
      'Real-world transfers rarely hit an interface\'s advertised maximum: protocol overhead, drive limits, and many small files all slow things down. Plan with sustained real-world rates, not the number on the box.',
    ],
    formula: {
      label: 'Transfer time from size and speed',
      expression: 'Time (s) = Data (GB) × 1,000 ÷ Speed (MB/s)',
      example: 'Example: 2 TB at a sustained 400 MB/s → 2,000 × 1,000 ÷ 400 = 5,000 s ≈ 1 h 23 min.',
    },
    table: {
      caption: 'Time to copy 1 TB at common sustained speeds',
      headers: ['Sustained speed', 'Typical of', 'Time for 1 TB'],
      rows: [
        ['100 MB/s', 'Spinning HDD, Gigabit Ethernet', '≈2 h 47 min'],
        ['250 MB/s', 'Fast HDD RAID, SATA SSD (slower)', '≈1 h 7 min'],
        ['500 MB/s', 'SATA SSD', '≈33 min'],
        ['1,000 MB/s', '10GbE, USB 3.2 Gen 2 NVMe', '≈17 min'],
        ['2,500 MB/s', 'Thunderbolt NVMe RAID', '≈7 min'],
      ],
      note: 'Sustained speeds are typically 60–80% of an interface\'s theoretical maximum.',
    },
    faqs: [
      {
        q: 'Why is my transfer slower than the advertised speed?',
        a: 'Interface ratings are theoretical bits-per-second maximums. Protocol overhead, the slower of the two drives, thermal throttling, and thousands-of-small-files workloads all reduce real throughput — 60–80% of the rated speed is a good planning number.',
      },
      {
        q: 'How long does it take to copy 1 TB over USB?',
        a: 'On USB 3.2 Gen 2 (10 Gb/s) with fast SSDs on both ends, roughly 17–25 minutes. Over older USB 3.0 with a spinning drive, closer to 3 hours. The drives usually matter more than the cable.',
      },
      {
        q: 'What is the fastest way to offload camera cards?',
        a: 'A fast card reader (CFexpress readers can exceed 1,000 MB/s) over Thunderbolt or USB 3.2 Gen 2, writing to an NVMe SSD or RAID. Checksummed offload tools add safety with some speed cost — budget for it.',
      },
      {
        q: 'How long does it take to upload footage to the cloud?',
        a: 'Divide the data by your upload speed, not your download speed: most cable and DSL plans upload far slower than they download. 100 GB at 35 Mb/s takes about 6 h 21 min; at 1 Gb/s, about 13 minutes. Choose an Internet option in the Interface menu and the calculator does the math, flagging the drive if it is the slower link.',
      },
      {
        q: 'Should I plan transfers with bits or bytes?',
        a: 'Network speeds are quoted in bits (10GbE = 10 gigabits/s ≈ 1,250 MB/s theoretical), drive speeds in bytes. This calculator accepts both units so you don\'t have to convert by hand.',
      },
    ],
    related: ['dailies', 'camera', 'duration'],
  },

  timecodecalculator: {
    intro: [
      'The Timecode Calculator adds, subtracts, multiplies, and divides SMPTE timecode at any professional frame rate — 23.976, 24, 25, 29.97 (drop and non-drop), 30, 50, 59.94, and more — with a calculator-style keypad and a running paper tape of your work.',
      'Timecode math trips people up because it is base-60 for hours, minutes and seconds but base-frame-rate for frames, and because 29.97 drop-frame renumbers frames to stay in sync with the clock. This tool handles those rules for you and converts between frame rates without losing frames.',
    ],
    formula: {
      label: 'Timecode to total frames',
      expression: 'Total frames = ((hours × 3,600 + minutes × 60 + seconds) × frame rate) + frames',
      example: 'Example: 01:00:00:00 at 24 fps = 86,400 frames.',
    },
    table: {
      caption: 'Common professional frame rates',
      headers: ['Frame rate', 'Where it\'s used'],
      rows: [
        ['23.976', 'Digital cinema-style acquisition for TV/streaming delivery'],
        ['24', 'Theatrical film and DCPs'],
        ['25', 'PAL-region broadcast and production'],
        ['29.97 DF/NDF', 'NTSC-region broadcast; drop-frame keeps clock time'],
        ['50 / 59.94', 'Sports, live production, high-motion content'],
      ],
    },
    faqs: [
      {
        q: 'What is drop-frame timecode?',
        a: 'At 29.97 fps, timecode counting 30 frames per second drifts about 3.6 seconds per hour versus real time. Drop-frame timecode skips frame numbers 00 and 01 at the start of each minute (except every tenth minute) so the timecode display matches wall-clock time. No actual video frames are dropped — only numbers.',
      },
      {
        q: 'How do I add two timecodes together?',
        a: 'Convert each timecode to total frames at your frame rate, add them, then convert back. That\'s exactly what this calculator does — enter the first timecode, press +, enter the second, and press =.',
      },
      {
        q: 'Why does 23.976 fps exist instead of just 24?',
        a: 'When color was added to NTSC television, the field rate was slowed by a factor of 1000/1001 to avoid interference, giving 29.97 fps. Film content transferred into that system inherited the same ratio: 24 × 1000/1001 = 23.976. It survives today for broadcast compatibility.',
      },
      {
        q: 'Can I convert a timecode from one frame rate to another?',
        a: 'Yes — but be clear about intent. Keeping the same frame count changes the real-time duration; keeping the same duration changes the frame count. This calculator supports frame-accurate conversion between any two supported rates.',
      },
    ],
    related: ['film', 'duration', 'aspectRatio'],
  },

  aspectRatio: {
    intro: [
      'The Aspect Ratio Calculator converts between aspect ratios, resolutions, and pixel dimensions. Enter any two values — say, a target width and a ratio — and it computes the rest, including matching common delivery resolutions and anamorphic desqueeze math.',
      'It covers the ratios working professionals actually deliver: 16:9 broadcast and streaming, 2.39:1 scope, 1.85:1 flat, 4:3 archival, vertical 9:16 social formats, and custom ratios for LED walls and specialty screens.',
    ],
    formula: {
      label: 'Height from width and ratio',
      expression: 'Height = Width ÷ Aspect Ratio',
      example: 'Example: 3840 wide at 2.39:1 → 3840 ÷ 2.39 ≈ 1607 px tall.',
    },
    table: {
      caption: 'Common aspect ratios and typical resolutions',
      headers: ['Ratio', 'Name / use', 'Example resolution'],
      rows: [
        ['16:9 (1.78:1)', 'HD/UHD broadcast & streaming', '3840 × 2160'],
        ['1.85:1', '"Flat" theatrical', '3996 × 2160 (DCI)'],
        ['2.39:1', '"Scope" widescreen', '4096 × 1716 (DCI)'],
        ['4:3 (1.33:1)', 'SD archival, some art-house', '1440 × 1080'],
        ['9:16 (0.56:1)', 'Vertical social video', '1080 × 1920'],
        ['2:1', 'Streaming-era "Univisium"', '4096 × 2048'],
      ],
    },
    faqs: [
      {
        q: 'What resolution is 2.39:1 at 4K?',
        a: 'For DCI 4K delivery, scope is 4096 × 1716. Cropped from a UHD frame, 2.39:1 is 3840 × 1607 (usually rounded to an even 3840 × 1608). The calculator gives you exact and even-number-rounded dimensions.',
      },
      {
        q: 'What is the difference between 4K and UHD?',
        a: 'DCI 4K is 4096 pixels wide (cinema standard); UHD is 3840 × 2160 (the consumer "4K" used by TVs and streaming). They differ in width and native aspect ratio — 1.90:1 versus 16:9.',
      },
      {
        q: 'How does anamorphic desqueeze work?',
        a: 'An anamorphic lens optically squeezes a wide image onto the sensor; in post you multiply the recorded width by the squeeze factor. A 2× anamorphic on a 4:3 sensor yields 2.66:1, which is typically cropped to 2.39:1 for delivery.',
      },
      {
        q: 'Why do my pixel dimensions need to be even numbers?',
        a: 'Most video codecs encode color in 2×2 pixel blocks (4:2:0 chroma subsampling), so odd dimensions are invalid or get silently padded. This calculator can round to even values for you.',
      },
    ],
    related: ['framingChartGenerator', 'camera', 'bitrate'],
  },

  film: {
    intro: [
      'The Motion Picture Film Calculator converts between film length in feet, running time, and number of rolls for 16mm and 35mm production. Enter a footage amount to get runtime, or a target runtime to see how much stock you need to order.',
      'Film math is fixed by sprocket geometry: 35mm 4-perf runs 16 frames per foot, 16mm runs 40 frames per foot. From there, frame rate determines everything — at 24 fps, 35mm 4-perf burns 90 feet per minute while 16mm uses just 36.',
    ],
    formula: {
      label: 'Runtime from footage (35mm 4-perf @ 24 fps)',
      expression: 'Runtime (min) = Feet ÷ 90   (16mm: Feet ÷ 36)',
      example: 'Example: a 1,000 ft roll of 35mm at 24 fps ≈ 11 minutes of runtime.',
    },
    table: {
      caption: 'Film usage at 24 fps',
      headers: ['Format', 'Frames per foot', 'Feet per minute', 'Runtime per 1,000 ft'],
      rows: [
        ['35mm 4-perf', '16', '90', '≈11.1 min'],
        ['35mm 3-perf', '21.3', '67.5', '≈14.8 min'],
        ['35mm 2-perf', '32', '45', '≈22.2 min'],
        ['16mm / Super 16', '40', '36', '≈27.8 min'],
      ],
    },
    faqs: [
      {
        q: 'How long does a 1,000 ft roll of 35mm last?',
        a: 'About 11 minutes at 24 fps in standard 4-perf. 3-perf stretches the same roll to roughly 15 minutes, and 2-perf to about 22 — one reason those formats are chosen for budget-conscious film shoots.',
      },
      {
        q: 'How much film stock does a feature need?',
        a: 'Multiply target runtime by your shooting ratio. A 100-minute film shot at a 10:1 ratio needs 1,000 minutes of stock — about 90,000 ft of 35mm 4-perf. The calculator converts your ratio and format into feet and rolls.',
      },
      {
        q: 'What is the difference between 2-perf, 3-perf, and 4-perf?',
        a: 'The number of perforations advanced per frame on 35mm. Fewer perfs per frame means a shorter, wider negative area and less stock consumed per minute — 3-perf saves about 25% versus 4-perf and suits 16:9 and scope extraction well.',
      },
      {
        q: 'Does shooting at higher frame rates use more film?',
        a: 'Yes, linearly: 48 fps consumes exactly twice the stock of 24 fps. Slow-motion work on film is budgeted in feet per second, not per minute.',
      },
    ],
    related: ['timecodecalculator', 'dailies', 'duration'],
  },

  dailies: {
    intro: [
      'The Dailies Calculator sizes the full data footprint of a production day: original camera files across all cameras, proxy/editorial transcodes, and the redundant copies your backup policy requires. It answers the producer question — "how much storage do we actually need to buy?" — with real numbers.',
      'A common mistake is budgeting only for original camera files. A sound 3-2-1 style policy (multiple copies on set, a shuttle drive to post, and an archive copy) multiplies every shot terabyte by three or more before editorial even starts.',
    ],
    formula: {
      label: 'Total production storage',
      expression: 'Total = Daily camera data × Shoot days × Number of copies (+ proxies)',
      example: 'Example: 2 TB/day × 20 days × 3 copies = 120 TB before proxies.',
    },
    table: {
      caption: 'Example: one 2 TB shoot day through a typical pipeline',
      headers: ['Copy', 'Where it lives', 'Running total'],
      rows: [
        ['Original offload', 'On-set RAID', '2 TB'],
        ['Second copy', 'Shuttle drive to post', '4 TB'],
        ['Archive copy', 'LTO tape or cloud', '6 TB'],
        ['Editorial proxies', 'Edit shared storage', '+ ~5–10% of OCF'],
      ],
    },
    faqs: [
      {
        q: 'What are dailies?',
        a: 'Dailies are the viewable copies of each day\'s footage — traditionally processed overnight so the director and studio could watch "yesterday\'s work daily." Today the term covers the whole nightly pipeline: offload, backup, color-managed transcodes, and delivery to editorial and review platforms.',
      },
      {
        q: 'How much storage does a shoot day generate?',
        a: 'Anywhere from a few hundred gigabytes on a documentary mirrorless package to 6+ TB on a multi-camera RAW narrative shoot — before copies. Use the Camera ↔︎ File Size calculator for per-camera estimates, then multiply by your backup policy here.',
      },
      {
        q: 'What is the 3-2-1 backup rule?',
        a: 'Keep at least 3 copies of the data, on 2 different types of media, with 1 copy off-site. On a production that typically means the on-set RAID, a shuttle drive, and an LTO or cloud archive — no camera card gets formatted until all copies verify.',
      },
      {
        q: 'How big should editorial proxies be?',
        a: 'Common proxy formats (ProRes Proxy or LT, DNxHR LB) run roughly 5–15% of original camera file size depending on the OCF codec. Heavier "online-quality" transcodes can approach or exceed OCF size — decide before you buy drives.',
      },
    ],
    related: ['camera', 'transferTime', 'duration'],
  },

  framingChartGenerator: {
    intro: [
      'The Framing Chart Generator creates production-ready framing charts: precise overlays of your capture format, extraction ratios, safe areas, and center marks, exportable as high-resolution files to shoot in front of the camera or load as monitor overlays.',
      'A framing chart shot on day one is the reference that keeps everyone honest downstream — the DIT builds monitor frame lines from it, VFX uses it to verify plate framing and lens geometry, and the finishing suite uses it to confirm extractions match what the DP composed for.',
    ],
    faqs: [
      {
        q: 'What is a framing chart?',
        a: 'A printed or displayed chart, photographed through the production camera and lens, that marks the intended framing: full sensor area, the extraction ratio(s) being delivered, center cross, and safe areas. It travels with the footage as the authoritative framing reference.',
      },
      {
        q: 'Why shoot a framing chart on set?',
        a: 'Because framing intent doesn\'t survive by word of mouth. A chart shot through the actual optics captures exactly what the camera saw, so dailies, VFX, and finishing can all align their extractions to the same reference instead of guessing.',
      },
      {
        q: 'What are action safe and title safe areas?',
        a: 'Margins inside the picture edge that keep important content clear of overscan and platform UI. Modern HD/UHD practice commonly uses around 93% of the frame for action safe and 90% for title safe, though delivery specs vary — the generator lets you set exact percentages.',
      },
      {
        q: 'Can I make charts for anamorphic formats?',
        a: 'Yes. The generator handles anamorphic squeeze factors and can bundle chart variants (squeezed and desqueezed) so both the camera setup and the monitoring pipeline can be verified end to end.',
      },
    ],
    related: ['aspectRatio', 'camera', 'timecodecalculator'],
  },
};

// Related-link helper: resolves calculator keys to path + label using calculatorConfig.
export const getRelatedLinks = (calculatorKey, calculatorConfig) => {
  const entry = seoContent[calculatorKey];
  if (!entry || !entry.related) return [];
  return entry.related
    .map((key) => {
      const calc = calculatorConfig[key];
      if (!calc) return null;
      return { key, path: calc.path, title: calc.title, description: calc.description };
    })
    .filter(Boolean);
};

// FAQPage JSON-LD for a set of FAQs (must mirror visible on-page content).
export const buildFaqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

export default seoContent;
